import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "clientEmail", "form", "documentCheckbox", "topicCheckbox", "transcriptsContainer", "newTranscriptTitle", "newTranscriptContent"]
  static values = {
    updateUrl: String,
    transcriptsUrl: String,
    practiceId: String,
    invitationId: String
  }

  connect() {
    this.modalTarget.addEventListener('turbo:submit-end', (event) => {
      if (event.detail.success) {
        this.close()
      }
    })
    this.transcripts = []
    this.lockedDocumentCheckbox = null
    this.setupFormSubmit()
  }

  setupFormSubmit() {
    this.formTarget.addEventListener('submit', () => {
      if (this.lockedDocumentCheckbox) {
        this.lockedDocumentCheckbox.disabled = false
      }
    })
  }

  open(event) {
    event.preventDefault()

    const button = event.currentTarget
    const clientEmail = button.dataset.clientEmail
    const selectedDocs = JSON.parse(button.dataset.selectedDocuments || '[]')
    const selectedTopics = JSON.parse(button.dataset.selectedTopics || '[]')
    const updateUrl = button.dataset.updateUrl
    const transcripts = JSON.parse(button.dataset.transcripts || '[]')
    this.practiceIdValue = button.dataset.practiceId
    this.invitationIdValue = button.dataset.invitationId

    this.clientEmailTarget.textContent = clientEmail
    this.formTarget.action = updateUrl
    this.transcripts = transcripts

    this.documentCheckboxTargets.forEach(checkbox => {
      checkbox.checked = selectedDocs.includes(parseInt(checkbox.value))
    })

    if (this.hasTopicCheckboxTarget) {
      this.topicCheckboxTargets.forEach(checkbox => {
        checkbox.checked = selectedTopics.includes(parseInt(checkbox.value))
      })
    }

    this.initializeDocumentLocking()
    this.renderTranscripts()
    this.clearNewTranscriptForm()

    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  initializeDocumentLocking() {
    this.updateDocumentLockState()
  }

  updateDocumentLockState() {
    const checkedBoxes = this.documentCheckboxTargets.filter(cb => cb.checked)

    if (this.lockedDocumentCheckbox) {
      this.unlockDocumentCheckbox(this.lockedDocumentCheckbox)
    }

    if (checkedBoxes.length === 1) {
      this.lockDocumentCheckbox(checkedBoxes[0])
    } else {
      this.lockedDocumentCheckbox = null
    }
  }

  toggleDocument(event) {
    const checkbox = event.currentTarget

    if (checkbox === this.lockedDocumentCheckbox) {
      event.preventDefault()
      checkbox.checked = true
      return
    }

    this.updateDocumentLockState()
  }

  lockDocumentCheckbox(checkbox) {
    if (this.lockedDocumentCheckbox) {
      this.unlockDocumentCheckbox(this.lockedDocumentCheckbox)
    }

    this.lockedDocumentCheckbox = checkbox
    checkbox.checked = true
    checkbox.disabled = true

    const label = checkbox.closest('label')
    if (label) {
      label.classList.add('opacity-60')
      label.classList.remove('hover:bg-gray-50', 'cursor-pointer')

      let indicator = label.querySelector('.locked-indicator')
      if (!indicator) {
        indicator = document.createElement('span')
        indicator.className = 'locked-indicator ml-2 text-xs text-gray-500 italic'
        indicator.textContent = '(Required)'
        label.appendChild(indicator)
      }
    }
  }

  unlockDocumentCheckbox(checkbox) {
    checkbox.disabled = false

    const label = checkbox.closest('label')
    if (label) {
      label.classList.remove('opacity-60')
      label.classList.add('hover:bg-gray-50', 'cursor-pointer')

      const indicator = label.querySelector('.locked-indicator')
      if (indicator) {
        indicator.remove()
      }
    }
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  renderTranscripts() {
    const container = this.transcriptsContainerTarget
    container.innerHTML = ''

    if (this.transcripts.length === 0) {
      container.innerHTML = '<p class="text-sm text-gray-500 italic py-2">No transcripts added yet.</p>'
      return
    }

    this.transcripts.forEach((transcript, index) => {
      const div = document.createElement('div')
      div.className = 'transcript-item border border-gray-200 rounded-lg p-3 mb-3'
      div.dataset.transcriptId = transcript.id
      div.innerHTML = this.transcriptDisplayHTML(transcript, index)
      container.appendChild(div)
    })
  }

  transcriptDisplayHTML(transcript, index) {
    const title = transcript.title || `Transcript ${index + 1}`
    const content = transcript.content || ''

    return `
      <div class="transcript-display">
        <div class="flex items-center justify-between mb-2">
          <h4 class="text-sm font-medium text-gray-900">${this.escapeHtml(title)}</h4>
          <div class="flex items-center gap-2">
            <button type="button"
                    class="text-indigo-600 hover:text-indigo-700 text-xs font-medium"
                    data-action="click->client-documents-modal#editTranscript"
                    data-transcript-id="${transcript.id}">
              Edit
            </button>
            <button type="button"
                    class="text-red-600 hover:text-red-700 text-xs font-medium"
                    data-action="click->client-documents-modal#deleteTranscript"
                    data-transcript-id="${transcript.id}">
              Delete
            </button>
          </div>
        </div>
        <div class="text-sm text-gray-600 trix-content prose prose-sm max-w-none max-h-24 overflow-hidden">${content}</div>
      </div>
      <div class="transcript-edit hidden">
        <div class="mb-2">
          <input type="text"
                 class="transcript-title-input block w-full text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                 value="${this.escapeHtml(transcript.title || '')}"
                 placeholder="Title (optional)">
        </div>
        <div class="mb-3">
          <div class="border border-gray-300 rounded-lg shadow-sm overflow-hidden bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
            <input type="hidden" class="transcript-content-hidden" id="transcript_edit_${transcript.id}" value="${this.escapeHtml(transcript.content || '')}">
            <trix-editor input="transcript_edit_${transcript.id}"
                         class="trix-content prose prose-sm max-w-none p-3 min-h-[120px] focus:outline-none"
                         placeholder="Enter transcript content, notes, or relevant information..."></trix-editor>
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button type="button"
                  class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  data-action="click->client-documents-modal#cancelEditTranscript"
                  data-transcript-id="${transcript.id}">
            Cancel
          </button>
          <button type="button"
                  class="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  data-action="click->client-documents-modal#saveTranscript"
                  data-transcript-id="${transcript.id}">
            Save
          </button>
        </div>
      </div>
    `
  }

  editTranscript(event) {
    event.preventDefault()
    const transcriptId = event.currentTarget.dataset.transcriptId
    const item = this.transcriptsContainerTarget.querySelector(`[data-transcript-id="${transcriptId}"]`)
    if (item) {
      item.querySelector('.transcript-display').classList.add('hidden')
      item.querySelector('.transcript-edit').classList.remove('hidden')
    }
  }

  cancelEditTranscript(event) {
    event.preventDefault()
    const transcriptId = event.currentTarget.dataset.transcriptId
    const item = this.transcriptsContainerTarget.querySelector(`[data-transcript-id="${transcriptId}"]`)
    if (item) {
      item.querySelector('.transcript-display').classList.remove('hidden')
      item.querySelector('.transcript-edit').classList.add('hidden')

      const transcript = this.transcripts.find(t => t.id == transcriptId)
      if (transcript) {
        const titleInput = item.querySelector('.transcript-title-input')
        const hiddenInput = item.querySelector('.transcript-content-hidden')
        titleInput.value = transcript.title || ''
        hiddenInput.value = transcript.content || ''
      }
    }
  }

  async saveTranscript(event) {
    event.preventDefault()
    const transcriptId = event.currentTarget.dataset.transcriptId
    const item = this.transcriptsContainerTarget.querySelector(`[data-transcript-id="${transcriptId}"]`)
    const title = item.querySelector('.transcript-title-input').value
    const content = item.querySelector('.transcript-content-hidden').value

    const button = event.currentTarget
    button.disabled = true
    button.textContent = 'Saving...'

    try {
      const response = await fetch(this.transcriptUrl(transcriptId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({ transcript: { title, content } })
      })

      const data = await response.json()

      if (data.success) {
        const index = this.transcripts.findIndex(t => t.id == transcriptId)
        if (index !== -1) {
          this.transcripts[index] = data.transcript
        }
        this.renderTranscripts()
      } else {
        alert('Failed to save transcript: ' + (data.errors || []).join(', '))
      }
    } catch (error) {
      alert('Failed to save transcript. Please try again.')
    } finally {
      button.disabled = false
      button.textContent = 'Save'
    }
  }

  async deleteTranscript(event) {
    event.preventDefault()
    if (!confirm('Are you sure you want to delete this transcript?')) return

    const transcriptId = event.currentTarget.dataset.transcriptId
    const button = event.currentTarget
    button.disabled = true

    try {
      const response = await fetch(this.transcriptUrl(transcriptId), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        }
      })

      const data = await response.json()

      if (data.success) {
        this.transcripts = this.transcripts.filter(t => t.id != transcriptId)
        this.renderTranscripts()
      } else {
        alert('Failed to delete transcript.')
      }
    } catch (error) {
      alert('Failed to delete transcript. Please try again.')
    } finally {
      button.disabled = false
    }
  }

  async addNewTranscript(event) {
    event.preventDefault()
    const titleInput = this.newTranscriptTitleTarget
    const contentInput = this.newTranscriptContentTarget
    const title = titleInput.value
    const content = contentInput.value

    if (!content.trim()) {
      alert('Please enter transcript content.')
      return
    }

    const button = event.currentTarget
    button.disabled = true
    button.textContent = 'Saving...'

    try {
      const response = await fetch(this.newTranscriptUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({ transcript: { title, content } })
      })

      const data = await response.json()

      if (data.success) {
        this.transcripts.push(data.transcript)
        this.renderTranscripts()
        this.clearNewTranscriptForm()
      } else {
        alert('Failed to add transcript: ' + (data.errors || []).join(', '))
      }
    } catch (error) {
      alert('Failed to add transcript. Please try again.')
    } finally {
      button.disabled = false
      button.textContent = 'Add Transcript'
    }
  }

  clearNewTranscriptForm() {
    if (this.hasNewTranscriptTitleTarget) {
      this.newTranscriptTitleTarget.value = ''
    }
    if (this.hasNewTranscriptContentTarget) {
      this.newTranscriptContentTarget.value = ''
      const editor = this.element.querySelector('trix-editor[input="new_transcript_content"]')
      if (editor && editor.editor) {
        editor.editor.loadHTML('')
      }
    }
  }

  transcriptUrl(id) {
    return `/psychologists/practices/${this.practiceIdValue}/client_invitations/${this.invitationIdValue}/transcripts/${id}`
  }

  newTranscriptUrl() {
    return `/psychologists/practices/${this.practiceIdValue}/client_invitations/${this.invitationIdValue}/transcripts`
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  stripHtml(html) {
    const tmp = document.createElement('div')
    tmp.innerHTML = html || ''
    return tmp.textContent || tmp.innerText || ''
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}
